import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Authentication layout components
import CustomBasicLayout from "layouts/auth/components/CustomBasicLayout";

import authService from "services/auth-service";

function ForgotPassword() {
  const [isDemo, setIsDemo] = useState(false);
  const [notification, setNotification] = useState(false);
  const [input, setEmail] = useState({
    email: "",
  });
  const [error, setError] = useState({
    err: false,
    textError: "",
  });

  useEffect(() => {
    setIsDemo(process.env.REACT_APP_IS_DEMO === "true");
  }, []);

  const changeHandler = (e) => {
    setEmail({
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (input.email.trim().length === 0 || !input.email.trim().match(mailFormat)) {
      setError({ err: true, textError: "The email must be valid" });
      return;
    }

    try {
      if (isDemo == false) {
        // Use the new resetPasswordRequest method instead of the old forgotPassword
        await authService.resetPasswordRequest(input.email);
        setError({ err: false, textError: "" });
      }
      setNotification(true);
    } catch (err) {
      console.error(err);
      if (err.hasOwnProperty("errors")) {
        if (err.errors.hasOwnProperty("email")) {
          setError({ err: true, textError: err.errors.email[0] });
        } else {
          setError({ err: true, textError: "An error occurred. Please try again." });
        }
      } else if (err.response && err.response.data && err.response.data.message) {
        // Check for specific error messages from the backend
        if (err.response.data.message.includes("not found") || err.response.data.message.includes("not exist")) {
          // Even if the email doesn't exist, we show a generic message for security reasons
          setNotification(true);
        } else {
          setError({ err: true, textError: "An error occurred while sending the reset link. Please try again." });
        }
      } else {
        setError({ err: true, textError: "An error occurred while sending the reset link. Please try again." });
      }
      return null;
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 3,
      backgroundColor: "#f8f9fa",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#465475" },
      "&.Mui-focused fieldset": { borderColor: "#465475", borderWidth: 2 },
    },
  };

  return (
    <CustomBasicLayout>
      <Card sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: 3 }}>
        {/* Logo/Icon */}
        <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={80}
            height={80}
            borderRadius="50%"
            sx={{
              background: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              boxShadow: "0 4px 20px rgba(70, 84, 117, 0.3)",
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36, color: "white" }}>lock_reset</Icon>
          </MDBox>
          <MDTypography variant="h4" fontWeight="bold" color="dark">
            Reset Password
          </MDTypography>
          <MDTypography variant="body2" color="text" sx={{ mt: 0.5, textAlign: "center" }}>
            Enter your email and we'll send you a link to reset your password
          </MDTypography>
        </MDBox>

        {/* Notification */}
        {notification && (
          <MDAlert color="info" sx={{ mb: 3 }}>
            <MDTypography variant="body2" color="white">
              {isDemo
                ? "You can't update the password in the demo version"
                : "If your email is registered with us, you will receive a password reset link shortly. Please check your inbox and spam folder."}
            </MDTypography>
          </MDAlert>
        )}

        {/* Form */}
        <MDBox component="form" onSubmit={handleSubmit}>
          {/* Email */}
          <MDBox mb={3}>
            <MDTypography variant="button" fontWeight="medium" color="text" display="block" mb={1}>
              Email Address
            </MDTypography>
            <TextField
              fullWidth
              type="email"
              name="email"
              placeholder="you@example.com"
              value={input.email}
              onChange={changeHandler}
              error={error.err}
              helperText={error.err ? error.textError : ""}
              sx={inputStyles}
            />
          </MDBox>

          {/* Reset Button */}
          <MDButton
            variant="gradient"
            color="info"
            fullWidth
            size="large"
            type="submit"
            sx={{
              borderRadius: 3,
              py: 1.5,
              textTransform: "none",
              fontSize: "1rem",
            }}
          >
            Send Reset Link
          </MDButton>

          {/* Sign In Link */}
          <MDBox mt={3} textAlign="center">
            <MDTypography variant="button" color="text">
              Remember your password?{" "}
              <MDTypography
                component={Link}
                to="/login"
                variant="button"
                color="info"
                fontWeight="medium"
              >
                Sign in
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    </CustomBasicLayout>
  );
}

export default ForgotPassword;